import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

import { Message, MessageService } from 'primeng/api';

@Component({
  selector: 'app-chat-input-file-upload-assistant',
  templateUrl: './chat-input-file-upload-assistant.component.html',
  styleUrl: './chat-input-file-upload-assistant.component.scss',
})
export class ChatInputFileUploadAssistantComponent {
  public highlightSearchFiles = false;
  public highlightInterpreterFiles = false;
  public messages: Message[] = [];
  protected maxFiles = 20;
  protected totalSize: number = 0;
  protected readonly MAX_FILE_SIZE: number = 200 * 1024 * 1024;
  protected readonly MAX_TOTAL_SIZE: number = 512 * 1024 * 1024;

  @Input() acceptSearchFiles = '';
  @Input() acceptInterpreterFiles = '';
  @Output() fileDropSearchFiles = new EventEmitter<File[]>();
  @Output() fileDropInterpreterFiles = new EventEmitter<File[]>();

  @ViewChild('dropArea') dropArea!: ElementRef;

  constructor(private readonly messageService: MessageService) {}

  onDragOver(event: DragEvent) {
    event.preventDefault();
    if (!event.dataTransfer) return;

    const rect = this.dropArea.nativeElement.getBoundingClientRect();
    const mid = rect.left + rect.width / 2;
    const dragX = event.clientX;

    this.highlightSearchFiles = dragX < mid;
    this.highlightInterpreterFiles = dragX >= mid;
  }

  onDragLeave() {
    this.highlightSearchFiles = false;
    this.highlightInterpreterFiles = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.highlightSearchFiles = false;
    this.highlightInterpreterFiles = false;
    if (!event.dataTransfer?.files?.length) return;

    const rect = this.dropArea.nativeElement.getBoundingClientRect();
    const mid = rect.left + rect.width / 2;
    const dropX = event.clientX;
    const files = Array.from(event.dataTransfer.files);

    this.totalSize =
      files?.reduce((sum, current) => sum + current.size, 0) || 0;
    const filesWithoutDuplicities = Array.from(new Set([...files]));

    if (filesWithoutDuplicities.length > this.maxFiles) {
      filesWithoutDuplicities.splice(this.maxFiles);
      this.messageService.add({
        key: 'dragdrop',
        severity: 'error',
        summary: 'Max files exceeded',
        detail: `You can only upload ${this.maxFiles} files.`,
      });

      return;
    }

    if (!this.validateFilesSize(files)) {
      return;
    }

    if (dropX < mid) {
      this.checkType(files, this.acceptSearchFiles, (accepted) => {
        this.fileDropSearchFiles.emit(accepted);
      });
    } else {
      this.checkType(files, this.acceptInterpreterFiles, (accepted) => {
        this.fileDropInterpreterFiles.emit(accepted);
      });
    }
  }

  private isAccepted(file: File, accept: string): boolean {
    if (!accept || accept === '*/*') return true;
    const types = accept.split(',').map((t) => t.trim());
    return types.some((t) => {
      if (t.endsWith('/*')) {
        return file.type.startsWith(t.replace('/*', ''));
      }
      return file.type === t || file.name.toLowerCase().endsWith(t);
    });
  }

  private checkType(files: File[], acceptSearchFiles: string, callback: (accepted: File[]) => void) {
    const accepted = files.filter((f) => {
      const isAccepted = this.isAccepted(f, acceptSearchFiles);
      if (!isAccepted) {
        this.messageService.add({
          key: 'dragdrop',
          severity: 'error',
          summary: 'Invalid file type',
          detail: 'Please select supported file type',
        });
      }
      return isAccepted;
    });

    if (accepted.length) {
      callback(accepted);
    }
  }

  private validateFilesSize(files: File[]): boolean {
    let valid = true;
    files.forEach((file) => {
      if (file.size > this.MAX_FILE_SIZE) {
        this.messageService.add({
          key: 'dragdrop',
          severity: 'error',
          summary: 'Invalid file',
          detail: `${file.name} is too large (max ${(
            this.MAX_FILE_SIZE /
            1024 /
            1024
          ).toFixed(0)} MB)`,
        });
        valid = false;
      }
    });

    const totalSize = files.reduce((acc, file) => acc + file.size, 0);
    if (totalSize > this.MAX_TOTAL_SIZE) {
      this.messageService.add({
        key: 'dragdrop',
        severity: 'error',
        summary: 'Invalid files',
        detail: `Total size is too large (max ${(
          this.MAX_TOTAL_SIZE /
          1024 /
          1024
        ).toFixed(0)} MB)`,
      });
      valid = false;
    }

    return valid;
  }
}
